import React from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { ThemeProvider, useTheme } from "./context/ThemeContext";
import FloatingAIChat from "./components/FloatingAIChat";
import WhatsAppButton from "./components/WhatsAppButton";
import ScrollToTop from "./components/ScrollToTop";

// Public Pages
import HomePage from "./pages/HomePage";
import AIAgentsPage from "./pages/AIAgentsPage";
import ServicesPage from "./pages/ServicesPage";
import IndustriesPage from "./pages/IndustriesPage";
import TechnologyPage from "./pages/TechnologyPage";
import PricingPage from "./pages/PricingPage";
import StartProjectPage from "./pages/StartProjectPage";
import InsightsPage from "./pages/InsightsPage";
import ClientSuccessPage from "./pages/ClientSuccessPage";
import AboutPage from "./pages/AboutPage";
import ContactPage from "./pages/ContactPage";
import LoginPage from "./pages/LoginPage";
import ProposalViewPage from "./pages/ProposalViewPage";
import PrototypeViewerPage from "./pages/PrototypeViewerPage";
import OffshoreOutsourcingPage from "./pages/OffshoreOutsourcingPage";
import HowItWorksPage from "./pages/HowItWorksPage";
import ProgramsPage from "./pages/ProgramsPage";

// Dashboard Pages
import AdminDashboard from "./pages/AdminDashboard";
import UpworkScoutPage from "./pages/UpworkScoutPage";
import WorkspacePage from "./pages/WorkspacePage";
import WorkspaceChatPage from "./pages/WorkspaceChatPage";
import ProjectDetailPage from "./pages/ProjectDetailPage";

// Protected Route Component
function ProtectedRoute({ children, requireAdmin = false }) {
  const { user, loading, isSuperAdmin } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-[#00F0FF] border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (requireAdmin && !isSuperAdmin()) {
    return <Navigate to="/workspace" replace />;
  }

  return children;
}

// Auth Route - Redirect if already logged in
function AuthRoute({ children }) {
  const { user, loading, isSuperAdmin } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-[#00F0FF] border-t-transparent rounded-full" />
      </div>
    );
  }

  if (user) {
    return <Navigate to={isSuperAdmin() ? "/admin" : "/workspace"} replace />;
  }

  return children;
}

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<HomePage />} />
      
      {/* AI Agents */}
      <Route path="/ai-agents" element={<AIAgentsPage />} />
      <Route path="/ai-agents/:agentId" element={<AIAgentsPage />} />
      
      {/* Services */}
      <Route path="/services" element={<ServicesPage />} />
      <Route path="/services/:serviceId" element={<ServicesPage />} />
      
      {/* Industries */}
      <Route path="/industries" element={<IndustriesPage />} />
      <Route path="/industries/:industrySlug" element={<IndustriesPage />} />
      
      {/* Technology */}
      <Route path="/technology" element={<TechnologyPage />} />
      
      {/* Pricing */}
      <Route path="/pricing" element={<PricingPage />} />
      
      {/* Start Project - Conversational AI */}
      <Route path="/start-project" element={<StartProjectPage />} />

      {/* Public proposal view (no login required) */}
      <Route path="/proposal/:leadId" element={<ProposalViewPage />} />

      {/* Public prototype viewer (teaser is free, full is gated by auth at generation time) */}
      <Route path="/prototype-preview/:prototypeId" element={<PrototypeViewerPage />} />

      {/* Offshore Outsourcing (India) */}
      <Route path="/offshore-outsourcing" element={<OffshoreOutsourcingPage />} />
      <Route path="/offshore-development-india" element={<OffshoreOutsourcingPage />} />
      <Route path="/hire-developers-india" element={<OffshoreOutsourcingPage />} />
      <Route path="/it-outsourcing-india" element={<OffshoreOutsourcingPage />} />

      {/* Programs */}
      <Route path="/programs" element={<ProgramsPage />} />

      {/* How It Works */}
      <Route path="/how-it-works" element={<HowItWorksPage />} />
      
      {/* Content Pages */}
      <Route path="/insights" element={<InsightsPage />} />
      <Route path="/insights/:slug" element={<InsightsPage />} />
      <Route path="/client-success" element={<ClientSuccessPage />} />
      <Route path="/client-success/:caseId" element={<ClientSuccessPage />} />
      <Route path="/about" element={<AboutPage />} />
      <Route path="/contact" element={<ContactPage />} />
      
      {/* Placeholder pages */}
      <Route path="/careers" element={<PlaceholderPage title="Careers" />} />
      <Route path="/privacy" element={<PlaceholderPage title="Privacy Policy" />} />
      <Route path="/terms" element={<PlaceholderPage title="Terms of Service" />} />
      <Route path="/security" element={<PlaceholderPage title="Security" />} />
      
      {/* SEO Landing Pages (catch-all pattern) */}
      <Route path="/hire-/*" element={<SEOLandingPage />} />
      <Route path="/mvp-/*" element={<SEOLandingPage />} />
      <Route path="/saas-/*" element={<SEOLandingPage />} />
      <Route path="/offshore-/*" element={<SEOLandingPage />} />
      <Route path="/outsource-/*" element={<SEOLandingPage />} />
      
      {/* Auth Routes */}
      <Route
        path="/login"
        element={
          <AuthRoute>
            <LoginPage />
          </AuthRoute>
        }
      />
      
      {/* Protected Routes */}
      <Route
        path="/workspace"
        element={
          <ProtectedRoute>
            <WorkspaceChatPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/workspace/billing"
        element={
          <ProtectedRoute>
            <WorkspacePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/workspace/project/:leadId"
        element={
          <ProtectedRoute>
            <ProjectDetailPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/workspace/:leadId"
        element={
          <ProtectedRoute>
            <WorkspaceChatPage />
          </ProtectedRoute>
        }
      />
      <Route path="/dashboard" element={<Navigate to="/workspace" replace />} />
      
      <Route
        path="/admin"
        element={
          <ProtectedRoute requireAdmin>
            <AdminDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/upwork"
        element={
          <ProtectedRoute requireAdmin>
            <UpworkScoutPage />
          </ProtectedRoute>
        }
      />
      
      {/* Catch all - redirect to home */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

// Placeholder for pages not yet built
function PlaceholderPage({ title }) {
  return (
    <div className="min-h-screen theme-bg flex items-center justify-center" style={{ background: 'var(--background)', color: 'var(--foreground)' }}>
      <div className="text-center">
        <h1 className="font-heading text-4xl font-bold mb-4">{title}</h1>
        <p className="theme-text-muted mb-4">Coming soon...</p>
        <a href="/" className="hover:underline" style={{ color: 'var(--primary)' }}>Back to Home</a>
      </div>
    </div>
  );
}

// SEO Landing Page Component - Enhanced
function SEOLandingPage() {
  const path = window.location.pathname;
  
  // Parse the URL to extract meaningful content
  let title = '';
  let description = '';
  let keywords = [];
  
  if (path.includes('hire-')) {
    const parts = path.replace('/hire-', '').replace(/-/g, ' ');
    title = `Hire ${parts} | XceedAgents.ai`;
    description = `Looking to hire ${parts}? XceedAgents provides AI-accelerated development teams. 60% faster delivery, enterprise quality.`;
    keywords = parts.split(' ');
  } else if (path.includes('outsource-')) {
    title = 'Outsource Software Development | XceedAgents.ai';
    description = 'Outsource your software development to AI-powered teams. Focus on building your business while we handle the code.';
    keywords = ['outsource', 'software', 'development'];
  } else if (path.includes('offshore-')) {
    title = 'Offshore Development Company | XceedAgents.ai';
    description = 'Offshore software development with AI acceleration. Global delivery, US timezone availability.';
    keywords = ['offshore', 'development', 'global'];
  } else {
    title = 'Software Development Services | XceedAgents.ai';
    description = 'AI-accelerated software development services for startups and enterprises.';
    keywords = ['software', 'development'];
  }
  
  return (
    <div className="min-h-screen bg-[#050505] text-white">
      {/* Simple SEO Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-[#050505]/80 backdrop-blur-xl border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <a href="/" className="flex items-center gap-2">
            <div className="w-10 h-10 bg-gradient-to-br from-[#00F0FF] to-[#7000FF] flex items-center justify-center">
              <span className="text-black font-bold">X</span>
            </div>
            <span className="font-heading font-bold text-xl">XceedAgents</span>
          </a>
          <a href="/start-project">
            <button className="bg-[#00F0FF] text-black font-bold px-6 py-2">
              Get Started
            </button>
          </a>
        </div>
      </header>

      <div className="pt-32 pb-24">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h1 className="font-heading text-4xl md:text-6xl font-bold mb-6 capitalize">
            {title.replace(' | XceedAgents.ai', '')}
          </h1>
          <p className="text-xl text-slate-400 mb-10 max-w-2xl mx-auto">
            {description}
          </p>
          
          {/* Features */}
          <div className="grid md:grid-cols-3 gap-6 mb-12">
            {[
              { title: '60% Faster', desc: 'AI-accelerated development' },
              { title: 'Enterprise Quality', desc: 'Senior engineers + AI agents' },
              { title: 'Global Delivery', desc: 'US, UK, EU, AU coverage' },
            ].map((item, i) => (
              <div key={i} className="p-6 border border-white/10 bg-[#0A0A0A]">
                <div className="text-2xl font-bold text-[#00F0FF] mb-2">{item.title}</div>
                <p className="text-sm text-slate-500">{item.desc}</p>
              </div>
            ))}
          </div>
          
          <a href="/start-project">
            <button className="bg-[#00F0FF] text-black font-bold h-14 px-12 uppercase tracking-wide hover:shadow-[0_0_30px_rgba(0,240,255,0.5)] transition-all">
              Start Your Project Today
            </button>
          </a>
          
          {/* Trust Badges */}
          <div className="mt-12 flex flex-wrap justify-center gap-6 text-sm text-slate-600">
            <span>✓ GDPR Compliant</span>
            <span>✓ NDA Ready</span>
            <span>✓ Full Code Ownership</span>
            <span>✓ US Timezone</span>
          </div>
        </div>
      </div>

      {/* Simple Footer */}
      <footer className="border-t border-white/10 py-8">
        <div className="max-w-7xl mx-auto px-6 text-center text-sm text-slate-600">
          © 2026 XceedAgents.ai. AI-Accelerated Software Development.
        </div>
      </footer>
    </div>
  );
}

function ThemedToaster() {
  const { isDark } = useTheme();
  return (
    <Toaster 
      position="top-right"
      toastOptions={{
        style: isDark ? {
          background: '#0F0F0F',
          border: '1px solid rgba(255,255,255,0.1)',
          color: '#fff',
        } : {
          background: '#FFFFFF',
          border: '1px solid rgba(0,0,0,0.1)',
          color: '#0F172A',
        },
      }}
    />
  );
}

// Hide the floating marketing chat inside the workspace (it has its own build chat)
function GlobalFloatingChat() {
  const location = useLocation();
  if (location.pathname.startsWith('/workspace')) return null;
  return <FloatingAIChat />;
}

function App() {
  return (
    <BrowserRouter>
      <ThemeProvider>
        <AuthProvider>
          <ScrollToTop />
          <AppRoutes />
          <GlobalFloatingChat />
          <WhatsAppButton />
          <ThemedToaster />
        </AuthProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}

export default App;
